import { alpha, createTheme } from '@mui/material/styles';

import appThemeV2Old from './themeV2Old';

/**
 * Admin / internal theme (dashboard, back-office, analytics, admin panel).
 *
 * Extends `appThemeV2Old` — only overrides that change the surface language live
 * here. Typography, semantic colours, chips, progress, and app-bar come from v2.
 *
 * Visual source: eligibility / ticket / calendar reminder shells.
 * No translateY hover lift — shadow and border only.
 */
const PRIMARY_MAIN = '#8353e2';
const PRIMARY_DARK = '#6d41d8';
const PRIMARY_LIGHT = '#9b75e8';

const PAGE_BACKGROUND = '#f8f7fc';
const PAGE_BACKGROUND_IMAGE = [
    'radial-gradient(circle at top right, rgba(131, 83, 226, 0.10), transparent 36%)',
    'radial-gradient(circle at 8% 0%, rgba(99, 102, 241, 0.06), transparent 28%)',
    'linear-gradient(180deg, #f8f7fc 0%, #f4f1fb 100%)',
].join(', ');

// Gradient washes through translucent white inputs and looks off on forms.
// const MODAL_BACKGROUND = [
//     'radial-gradient(circle at top right, rgba(131, 83, 226, 0.12), transparent 34%)',
//     'linear-gradient(180deg, #ffffff 0%, #fcfbff 100%)',
// ].join(', ');

const INPUT_BACKGROUND = 'rgba(255, 255, 255, 0.9)';
const PRIMARY_GRADIENT = `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${PRIMARY_DARK} 100%)`;
const PRIMARY_GRADIENT_HOVER = `linear-gradient(135deg, ${PRIMARY_LIGHT} 0%, ${PRIMARY_MAIN} 100%)`;

const SHADOW_INPUT = '0 8px 24px rgba(15, 23, 42, 0.04)';
const SHADOW_INPUT_HOVER = '0 12px 24px rgba(15, 23, 42, 0.06)';
const SHADOW_INPUT_FOCUS = '0 0 0 4px rgba(131, 83, 226, 0.08)';
const SHADOW_BUTTON = '0 12px 24px rgba(131, 83, 226, 0.22)';
const SHADOW_BUTTON_HOVER = '0 14px 28px rgba(131, 83, 226, 0.28)';
const SHADOW_BUTTON_OUTLINED = '0 10px 24px rgba(15, 23, 42, 0.06)';
const SHADOW_MODAL = '0 18px 44px rgba(15, 23, 42, 0.08), 0 2px 10px rgba(15, 23, 42, 0.04)';
const SHADOW_MENU = '0 18px 44px rgba(15, 23, 42, 0.10), 0 2px 10px rgba(15, 23, 42, 0.04)';
const SHADOW_CARD = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
const SHADOW_CARD_HOVER = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';

const SURFACE_TRANSITION = 'box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease';

const menuPaper = {
    borderRadius: 14,
    border: '1px solid rgba(148, 163, 184, 0.18)',
    boxShadow: SHADOW_MENU,
};

const containedPrimary = {
    backgroundImage: PRIMARY_GRADIENT,
    backgroundColor: PRIMARY_MAIN,
    boxShadow: SHADOW_BUTTON,
    '&:hover': {
        backgroundImage: PRIMARY_GRADIENT_HOVER,
        backgroundColor: PRIMARY_LIGHT,
        boxShadow: SHADOW_BUTTON_HOVER,
    },
    '&:active': {
        boxShadow: SHADOW_BUTTON,
    },
    '&.Mui-disabled': {
        backgroundImage: 'none',
    },
};

const appThemeV2 = createTheme(appThemeV2Old, {
    palette: {
        primary: {
            main: PRIMARY_MAIN,
            light: PRIMARY_LIGHT,
            dark: PRIMARY_DARK,
            contrastText: '#ffffff',
        },
        background: {
            default: PAGE_BACKGROUND,
            paper: '#ffffff',
        },
    },
    surfaces: {
        pageBackgroundImage: PAGE_BACKGROUND_IMAGE,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: 14,
                    fontWeight: 600,
                    letterSpacing: '0.01em',
                    boxShadow: 'none',
                    transition: SURFACE_TRANSITION,
                },
                sizeSmall: {
                    height: 36,
                    padding: '0 14px',
                    borderRadius: 12,
                    fontSize: '0.8125rem',
                },
                sizeMedium: {
                    height: 44,
                    padding: '0 20px',
                    fontSize: '0.875rem',
                },
                sizeLarge: {
                    height: 48,
                    padding: '0 24px',
                    fontSize: '1rem',
                },
                contained: containedPrimary,
                containedPrimary,
                outlined: {
                    borderColor: 'rgba(148, 163, 184, 0.24)',
                    backgroundColor: 'rgba(255, 255, 255, 0.88)',
                    boxShadow: SHADOW_BUTTON_OUTLINED,
                    color: '#475569',
                    '&:hover': {
                        borderColor: alpha(PRIMARY_MAIN, 0.28),
                        backgroundColor: '#ffffff',
                        boxShadow: '0 12px 24px rgba(15, 23, 42, 0.08)',
                    },
                },
                outlinedPrimary: {
                    borderColor: alpha(PRIMARY_MAIN, 0.32),
                    color: PRIMARY_DARK,
                    '&:hover': {
                        borderColor: PRIMARY_MAIN,
                        backgroundColor: alpha(PRIMARY_MAIN, 0.06),
                    },
                },
                text: {
                    '&:hover': {
                        backgroundColor: alpha(PRIMARY_MAIN, 0.08),
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    transition: SURFACE_TRANSITION,
                    '&:hover': {
                        backgroundColor: alpha(PRIMARY_MAIN, 0.08),
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 14,
                    backgroundColor: INPUT_BACKGROUND,
                    boxShadow: SHADOW_INPUT,
                    transition: SURFACE_TRANSITION,
                    '&:hover': {
                        boxShadow: SHADOW_INPUT_HOVER,
                    },
                    '&.Mui-focused': {
                        boxShadow: SHADOW_INPUT_FOCUS,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(PRIMARY_MAIN, 0.32),
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 1,
                        borderColor: PRIMARY_MAIN,
                    },
                    '&.Mui-disabled': {
                        backgroundColor: 'rgba(248, 250, 252, 0.9)',
                        boxShadow: 'none',
                    },
                },
                notchedOutline: {
                    borderColor: 'rgba(148, 163, 184, 0.28)',
                },
                input: {
                    '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: '#64748b',
                    fontWeight: 500,
                    '&.Mui-focused': {
                        color: PRIMARY_DARK,
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginLeft: 4,
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#334155',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    border: '1px solid rgba(148, 163, 184, 0.14)',
                    boxShadow: SHADOW_CARD,
                    transition: SURFACE_TRANSITION,
                    '&:hover': {
                        boxShadow: SHADOW_CARD_HOVER,
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                rounded: {
                    borderRadius: 16,
                },
                elevation1: {
                    boxShadow: SHADOW_CARD,
                },
                elevation3: {
                    boxShadow: SHADOW_CARD_HOVER,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                root: {
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(15, 23, 42, 0.35)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                    },
                },
                paper: {
                    borderRadius: 28,
                    border: '1px solid rgba(131, 83, 226, 0.08)',
                    // Gradient washes through translucent white inputs and looks off.
                    // backgroundImage: MODAL_BACKGROUND,
                    backgroundColor: '#ffffff',
                    boxShadow: SHADOW_MODAL,
                    overflowX: 'hidden',
                    '@media (max-width: 600px)': {
                        '&:not(.MuiDialog-paperFullScreen)': {
                            borderRadius: 24,
                        },
                    },
                },
                paperFullScreen: {
                    borderRadius: 0,
                    border: 'none',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid rgba(148, 163, 184, 0.28)',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    color: '#111827',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    lineHeight: 1.3,
                    padding: '24px 24px 12px',
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: '16px 24px',
                },
                dividers: {
                    borderColor: 'rgba(148, 163, 184, 0.16)',
                    backgroundColor: 'rgba(248, 250, 252, 0.45)',
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    // Match add-note footer: px: 3, py: 2, gap: 1
                    padding: '16px 24px',
                    gap: 8,
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: menuPaper,
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: menuPaper,
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: 14,
                    color: '#111827',
                    borderRadius: 10,
                    margin: '2px 6px',
                    '&:hover': {
                        backgroundColor: alpha(PRIMARY_MAIN, 0.08),
                    },
                    '&.Mui-selected': {
                        backgroundColor: alpha(PRIMARY_MAIN, 0.12),
                        '&:hover': {
                            backgroundColor: alpha(PRIMARY_MAIN, 0.16),
                        },
                    },
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                paper: menuPaper,
                option: {
                    fontSize: 14,
                    '&[aria-selected="true"]': {
                        backgroundColor: alpha(PRIMARY_MAIN, 0.12),
                    },
                    '&.Mui-focused': {
                        backgroundColor: alpha(PRIMARY_MAIN, 0.08),
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    borderRadius: 10,
                    backgroundColor: '#1e293b',
                    fontSize: 12,
                    fontWeight: 500,
                    boxShadow: SHADOW_CARD_HOVER,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    boxShadow: 'none',
                },
                standardSuccess: {
                    backgroundColor: 'rgba(240, 253, 244, 0.88)',
                    border: '1px solid rgba(34, 197, 94, 0.18)',
                },
                standardError: {
                    backgroundColor: 'rgba(254, 242, 242, 0.92)',
                    border: '1px solid rgba(239, 68, 68, 0.18)',
                },
            },
        },
    },
});

export default appThemeV2;
